import { attach, combine, createEvent, createStore, sample } from 'effector';
import { spread } from 'patronum';
import { $$app } from 'components/App/model';
import { createFeedbackPanelModel } from 'components/FeedbackPanel/model';
import { $$campaignSetupService } from 'services/CampaignSetupService';
import type { CampaignMode, CampaignSetupJobOption, CampaignSetupOption, CreateFormData, EditFormData, StidMailerPair, StidSetupState } from './types';

//-------------------------------- Events --------------------------------

const pageMounted = createEvent<boolean>();
const pageUnmounted = pageMounted.filter({ fn: mounted => mounted === false });
const modeChanged = createEvent<CampaignMode>();

const createFormChanged = createEvent<Partial<CreateFormData>>();
const editFormChanged = createEvent<Partial<EditFormData>>();

const availableStidSelected = createEvent<string>();
const activeStidAdded = createEvent<void>();
const activeStidSelected = createEvent<string>();
const activeStidRemoved = createEvent<void>();
const activeMailerIdChanged = createEvent<string>();
const setAvailableMailerIds = createEvent<string[]>();
const stidPairSaved = createEvent<void>();
const stidPairRemoved = createEvent<number>();
const populateAvailableStids = createEvent<string[]>();

const submitButtonClicked = createEvent<void>();
const cancelButtonClicked = createEvent<void>();

//-------------------------------- Fx --------------------------------

const getClientOptionsFx = attach({ effect: $$campaignSetupService.getClientOptionsFx });
const getCloneCampaignOptionsFx = attach({ effect: $$campaignSetupService.getCloneCampaignOptionsFx });
const getEditCampaignOptionsFx = attach({ effect: $$campaignSetupService.getEditCampaignOptionsFx });
const getContactOptionsFx = attach({ effect: $$campaignSetupService.getContactOptionsFx });
const getLobOptionsFx = attach({ effect: $$campaignSetupService.getLobOptionsFx });
const getMailerIdOptionsFx = attach({ effect: $$campaignSetupService.getMailerIdOptionsFx });
const getJobContactFx = attach({ effect: $$campaignSetupService.getJobContactFx });
const getJobLobFx = attach({ effect: $$campaignSetupService.getJobLobFx });
const getJobServicesFx = attach({ effect: $$campaignSetupService.getJobServicesFx });
const submitCreateFx = attach({ effect: $$campaignSetupService.submitCreateFx });
const submitEditFx = attach({ effect: $$campaignSetupService.submitEditFx });

//-------------------------------- Stores --------------------------------

const $initialized = createStore(false)
  .on(pageMounted, (_, mounted) => mounted)
  .reset(pageUnmounted);
const $mode = createStore<CampaignMode>('create')
  .on(modeChanged, (_, mode) => mode)
  .reset(pageUnmounted);

const defaultCreateForm: CreateFormData = {
  clientId: null,
  campaignToCloneId: null,
  cloneDateRange: '30',
  campaignName: '',
  campaignNumber: '',
  lineOfBusinessId: null,
  contactId: null,
  clientPo: '',
  sendNotification: false,
  classOfMail: null,
  serviceType: null,
  acsEnabled: false,
  acsType: null,
  secureDestruction: false,
  createUniqueInboundImb: false,
  inboundImbType: null,
  nameAddressRequired: false
};

const defaultEditForm: EditFormData = {
  clientId: null,
  campaignName: null,
  campaignNumber: null
};

const defaultStidSetup: StidSetupState = {
  availableStids: [],
  selectedAvailableStid: null,
  activeStids: [],
  selectedActiveStid: null,
  activeMailerId: null,
  availableMailerIds: [],
  savedPairs: []
};

const $createForm = createStore<CreateFormData>(defaultCreateForm)
  .on(createFormChanged, (state, patch) => ({ ...state, ...patch }))
  .reset(pageUnmounted);

const $editForm = createStore<EditFormData>(defaultEditForm)
  .on(editFormChanged, (state, patch) => ({ ...state, ...patch }))
  .reset(pageUnmounted);

const $clientOptions = createStore<CampaignSetupOption[]>([]).reset(pageUnmounted);
const $cloneCampaignOptions = createStore<CampaignSetupJobOption[]>([]).reset(pageUnmounted);
const $editCampaignOptions = createStore<CampaignSetupJobOption[]>([]).reset(pageUnmounted);
const $contactOptions = createStore<CampaignSetupOption[]>([]).reset(pageUnmounted);
const $lobOptions = createStore<CampaignSetupOption[]>([]).reset(pageUnmounted);

const $stidSetup = createStore<StidSetupState>(defaultStidSetup)
  .on(availableStidSelected, (state, stid) => ({ ...state, selectedAvailableStid: stid }))
  .on(activeStidAdded, state => {
    if (!state.selectedAvailableStid) {
      return state;
    }
    const updatedAvailable = state.availableStids.filter(s => s !== state.selectedAvailableStid);
    const updatedActive = [...state.activeStids, state.selectedAvailableStid];
    return { ...state, availableStids: updatedAvailable, activeStids: updatedActive, selectedAvailableStid: null };
  })
  .on(activeStidSelected, (state, stid) => ({ ...state, selectedActiveStid: stid }))
  .on(activeStidRemoved, state => {
    if (!state.selectedActiveStid) {
      return state;
    }
    const updatedActive = state.activeStids.filter(s => s !== state.selectedActiveStid);
    const updatedAvailable = [...state.availableStids, state.selectedActiveStid];
    return { ...state, activeStids: updatedActive, availableStids: updatedAvailable, selectedActiveStid: null, activeMailerId: null };
  })
  .on(activeMailerIdChanged, (state, mailerId) => ({ ...state, activeMailerId: mailerId }))
  .on(setAvailableMailerIds, (state, mailerIds) => ({ ...state, availableMailerIds: mailerIds }))
  .on(populateAvailableStids, (state, stids) => {
    const availableStids = stids.filter(stid => !state.activeStids.includes(stid));
    const selectedAvailableStid = state.selectedAvailableStid && availableStids.includes(state.selectedAvailableStid) ? state.selectedAvailableStid : null;
    return { ...state, availableStids, selectedAvailableStid };
  })
  .on(stidPairSaved, state => {
    if (!state.selectedActiveStid || !state.activeMailerId) {
      return state;
    }
    const pair: StidMailerPair = { stid: state.selectedActiveStid, mailerId: state.activeMailerId };
    return { ...state, selectedActiveStid: null, activeMailerId: null, savedPairs: [...state.savedPairs, pair] };
  })
  .on(stidPairRemoved, (state, index) => ({
    ...state,
    savedPairs: state.savedPairs.filter((_, pairIndex) => pairIndex !== index)
  }))
  .reset(pageUnmounted);

const $isSubmitPending = combine(submitCreateFx.pending, submitEditFx.pending, (createPending, editPending) => createPending || editPending);

const feedbackPanel = createFeedbackPanelModel();

//-------------------------------- Events mapped from stores --------------------------------

const createClientIdSet = $createForm.map(formData => formData.clientId).updates.filter({ fn: clientId => clientId !== undefined });
const createCloneJobIdSet = $createForm.map(formData => formData.campaignToCloneId).updates.filter({ fn: campaignId => campaignId !== undefined });
const editClientIdSet = $editForm.map(formData => formData.clientId).updates.filter({ fn: clientId => clientId !== undefined });
const editCampaignNameSet = $editForm.map(formData => formData.campaignName).updates.filter({ fn: campaignName => campaignName !== undefined });
const editCampaignNumberSet = $editForm.map(formData => formData.campaignNumber).updates.filter({ fn: campaignNumber => campaignNumber !== undefined });

//-------------------------------- Samples --------------------------------

sample({
  clock: pageMounted.filter({ fn: Boolean }),
  target: getClientOptionsFx
});

sample({
  source: $$app.$userDetails,
  clock: pageMounted.filter({ fn: Boolean }),
  filter: userDetails => Boolean(userDetails?.isMt && userDetails?.activePersona?.vendorId == null),
  fn: userDetails => ({ clientId: String(userDetails?.activePersona?.ghsClientId ?? '') }),
  target: [createFormChanged, editFormChanged]
});

sample({
  clock: getClientOptionsFx.doneData,
  target: $clientOptions
});

sample({
  clock: createClientIdSet,
  filter: clientId => Boolean(clientId),
  fn: clientId => ({ clientId: clientId as string }),
  target: [getCloneCampaignOptionsFx, getContactOptionsFx, getLobOptionsFx, getMailerIdOptionsFx]
});

sample({
  clock: createClientIdSet,
  filter: clientId => clientId !== null,
  fn: () => ({ campaignToCloneId: null, contactId: null, lineOfBusinessId: null }),
  target: createFormChanged
});

sample({
  clock: editClientIdSet,
  filter: clientId => Boolean(clientId),
  fn: clientId => ({ clientId: clientId as string }),
  target: getEditCampaignOptionsFx
});

sample({
  clock: editClientIdSet,
  filter: clientId => clientId !== null,
  fn: () => ({ campaignName: null, campaignNumber: null }),
  target: editFormChanged
});

sample({
  clock: getCloneCampaignOptionsFx.doneData,
  target: $cloneCampaignOptions
});

sample({
  clock: getEditCampaignOptionsFx.doneData,
  target: $editCampaignOptions
});

sample({
  clock: getContactOptionsFx.doneData,
  target: $contactOptions
});

sample({
  clock: getLobOptionsFx.doneData,
  target: $lobOptions
});

sample({
  clock: getMailerIdOptionsFx.doneData,
  fn: options => options.map(option => option.label),
  target: setAvailableMailerIds
});

sample({
  clock: createCloneJobIdSet,
  filter: campaignToCloneId => Boolean(campaignToCloneId && campaignToCloneId !== '0'),
  fn: campaignToCloneId => ({ jobId: campaignToCloneId as string }),
  target: [getJobContactFx, getJobLobFx, getJobServicesFx]
});

sample({
  clock: createCloneJobIdSet,
  filter: campaignToCloneId => campaignToCloneId === '0',
  fn: () => ({
    contactId: null,
    lineOfBusinessId: null,
    classOfMail: null,
    serviceType: null,
    acsEnabled: false,
    acsType: null,
    secureDestruction: false,
    createUniqueInboundImb: false,
    inboundImbType: null,
    nameAddressRequired: false
  }),
  target: createFormChanged
});

sample({
  clock: getJobContactFx.doneData,
  fn: option => ({ contactId: option.id }),
  target: createFormChanged
});

sample({
  clock: getJobLobFx.doneData,
  fn: option => ({ lineOfBusinessId: option.id }),
  target: createFormChanged
});

sample({
  source: $editCampaignOptions,
  clock: editCampaignNameSet,
  filter: (_, campaignName) => Boolean(campaignName),
  fn: (campaignOptions, campaignName) => {
    const selectedCampaign = campaignOptions.find(option => option.name === campaignName);
    return selectedCampaign ? { campaignNumber: selectedCampaign.number } : {};
  },
  target: editFormChanged
});

sample({
  source: $editCampaignOptions,
  clock: editCampaignNumberSet,
  filter: (_, campaignNumber) => Boolean(campaignNumber),
  fn: (campaignOptions, campaignNumber) => {
    const selectedCampaign = campaignOptions.find(option => option.number === campaignNumber);
    return selectedCampaign ? { campaignName: selectedCampaign.name } : {};
  },
  target: editFormChanged
});

sample({
  clock: getJobServicesFx.doneData,
  fn: services => {
    let classOfMail: CreateFormData['classOfMail'] = null;
    if (services.firstClass) {
      classOfMail = '1st-class';
    } else if (services.standard) {
      classOfMail = 'marketing-mail';
    }

    let serviceType: CreateFormData['serviceType'] = null;
    if (services.fullService) {
      serviceType = 'full-service';
    } else if (services.basicService) {
      serviceType = 'basic-service';
    }

    let acsType: CreateFormData['acsType'] = null;
    if (services.asr) {
      acsType = 'asr';
    } else if (services.csr) {
      acsType = 'csr';
    }

    let inboundImbType: CreateFormData['inboundImbType'] = null;
    if (services.brm) {
      inboundImbType = 'brm';
    } else if (services.crm) {
      inboundImbType = 'crm';
    }

    return {
      classOfMail,
      serviceType,
      acsEnabled: services.acs,
      acsType,
      secureDestruction: services.secureDestruction,
      createUniqueInboundImb: services.brm || services.crm,
      inboundImbType,
      nameAddressRequired: services.nameAddressRequired
    };
  },
  target: createFormChanged
});

// Populate available STIDs when form changes
const serviceOptionsChanged = createFormChanged.filter({
  fn: patch => 'classOfMail' in patch || 'serviceType' in patch || 'acsEnabled' in patch || 'createUniqueInboundImb' in patch
});

sample({
  clock: serviceOptionsChanged,
  fn: () => {
    // Generate 10 random sample STIDs based on the form selections
    const stids: string[] = [];
    for (let i = 1; i <= 10; i++) {
      stids.push(`STID${String(i).padStart(4, '0')}`);
    }
    return stids;
  },
  target: populateAvailableStids
});

// Populate mailer IDs with sample data when an active STID is selected
sample({
  clock: activeStidSelected,
  fn: () => {
    // Generate sample Mailer IDs
    const mailerIds: string[] = [];
    const samples = ['123456', '765432', '001122', '556677'];
    mailerIds.push(...samples);
    return mailerIds;
  },
  target: setAvailableMailerIds
});

sample({
  clock: submitButtonClicked,
  source: { mode: $mode, createForm: $createForm, editForm: $editForm },
  filter: ({ mode }) => mode === 'create',
  fn: ({ createForm }) => createForm,
  target: submitCreateFx
});

sample({
  clock: submitButtonClicked,
  source: { mode: $mode, editForm: $editForm },
  filter: ({ mode }) => mode === 'edit',
  fn: ({ editForm }) => editForm,
  target: submitEditFx
});

sample({
  clock: cancelButtonClicked,
  source: $$app.$userDetails,
  fn: userDetails => {
    const defaultClientId = userDetails?.isMt && userDetails?.activePersona?.vendorId == null ? String(userDetails.activePersona.ghsClientId) : null;
    return {
      createForm: { ...defaultCreateForm, clientId: defaultClientId },
      editForm: { ...defaultEditForm, clientId: defaultClientId },
      feedbackData: null
    };
  },
  target: spread({
    createForm: createFormChanged,
    editForm: editFormChanged,
    feedbackData: feedbackPanel.setFeedbackData
  })
});

sample({
  clock: submitCreateFx.done,
  fn: () => ({
    feedbackData: { severity: 'success' as const, title: 'Campaign created', message: 'The campaign was created successfully.' }
  }),
  target: spread({ feedbackData: feedbackPanel.setFeedbackData })
});

sample({
  clock: submitCreateFx.fail,
  fn: () => ({
    feedbackData: { severity: 'error' as const, title: 'Failed to create campaign', message: 'An error occurred. Please try again.' }
  }),
  target: spread({ feedbackData: feedbackPanel.setFeedbackData })
});

sample({
  clock: submitEditFx.done,
  fn: () => ({
    feedbackData: { severity: 'success' as const, title: 'Campaign updated', message: 'The campaign was updated successfully.' }
  }),
  target: spread({ feedbackData: feedbackPanel.setFeedbackData })
});

sample({
  clock: submitEditFx.fail,
  fn: () => ({
    feedbackData: { severity: 'error' as const, title: 'Failed to update campaign', message: 'An error occurred. Please try again.' }
  }),
  target: spread({ feedbackData: feedbackPanel.setFeedbackData })
});

sample({
  clock: [getClientOptionsFx.fail, getCloneCampaignOptionsFx.fail, getEditCampaignOptionsFx.fail, getContactOptionsFx.fail, getLobOptionsFx.fail, getMailerIdOptionsFx.fail],
  fn: () => ({
    feedbackData: { severity: 'error' as const, title: 'Failed to load campaign setup data', message: 'We could not load the campaign setup options. Please try again.' }
  }),
  target: spread({ feedbackData: feedbackPanel.setFeedbackData })
});

export const $$campaignSetup = {
  $initialized,
  $mode,
  $createForm,
  $editForm,
  $clientOptions,
  $cloneCampaignOptions,
  $editCampaignOptions,
  $contactOptions,
  $lobOptions,
  $stidSetup,
  $isSubmitPending,
  feedbackPanel,
  pageMounted,
  modeChanged,
  createFormChanged,
  editFormChanged,
  availableStidSelected,
  activeStidAdded,
  activeStidSelected,
  activeStidRemoved,
  activeMailerIdChanged,
  stidPairSaved,
  stidPairRemoved,
  populateAvailableStids,
  submitButtonClicked,
  cancelButtonClicked
};
